import React, { useEffect, useRef } from 'react';
import { Mic, MicOff, Video as VideoIcon, VideoOff, Monitor, PhoneOff, AlertCircle, Phone } from 'lucide-react';
import { useGroupCall } from '../../context/GroupCallContext';
import { useAuth } from '../../context/AuthContext';
import { UserAvatar } from '../common/UserAvatar';

interface RemoteAudioPlayerProps {
  userId: string;
  stream: MediaStream | null;
}

const RemoteAudioPlayer: React.FC<RemoteAudioPlayerProps> = ({ userId, stream }) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = audioRef.current;
    if (el && stream) {
      console.log(`[GroupCall] REMOTE_AUDIO_PLAY_START for user ${userId}`, {
        streamId: stream.id,
        tracks: stream.getAudioTracks().map((t) => `${t.kind}(enabled=${t.enabled},state=${t.readyState})`),
      });
      el.srcObject = stream;
      el.volume = 1.0;
      el.muted = false;
      el.play()
        .then(() => {
          console.log(`[GroupCall] REMOTE_AUDIO_PLAY_SUCCESS for user ${userId}`);
        })
        .catch((err) => {
          console.warn(`[GroupCall] REMOTE_AUDIO_PLAY_FAILURE for user ${userId}:`, err);
        });
    }
  }, [userId, stream]);

  return <audio ref={audioRef} autoPlay playsInline className="hidden" />;
};

interface VideoTileProps {
  stream: MediaStream | null;
  displayName: string;
  avatarUrl?: string | undefined;
  isMuted: boolean;
  isVideoOff: boolean;
  isScreenSharing: boolean;
  isLocal?: boolean | undefined;
}

const VideoTile: React.FC<VideoTileProps> = ({
  stream,
  displayName,
  avatarUrl,
  isMuted,
  isVideoOff,
  isScreenSharing,
  isLocal,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <div className="relative bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex items-center justify-center min-h-[160px] max-h-[calc(100vh-180px)] aspect-video w-full">
      {/* Video Element */}
      {!isVideoOff && stream ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${isLocal && !isScreenSharing ? '-scale-x-100' : ''}`}
        />
      ) : (
        /* Avatar Placeholder when video is off */
        <div className="flex flex-col items-center gap-3 p-4">
          <UserAvatar
            displayName={displayName}
            avatarUrl={avatarUrl}
            size="xl"
            className="shadow-md"
          />
          <span className="text-xs text-zinc-400 font-medium">{displayName}</span>
        </div>
      )}

      {/* Top badges */}
      <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
        <span className="bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-md border border-white/10">
          {displayName} {isLocal && '(You)'}
        </span>
        {isScreenSharing && (
          <span className="bg-emerald-500/90 text-white text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 shadow-sm">
            <Monitor className="h-3 w-3" /> Sharing Screen
          </span>
        )}
      </div>

      {/* Mute Badge */}
      {isMuted && (
        <div className="absolute bottom-3 right-3 bg-red-500/90 text-white p-1.5 rounded-full shadow-lg z-10">
          <MicOff className="h-3.5 w-3.5" />
        </div>
      )}
    </div>
  );
};

interface AudioTileProps {
  displayName: string;
  avatarUrl?: string | undefined;
  isMuted: boolean;
  isLocal?: boolean | undefined;
}

const AudioTile: React.FC<AudioTileProps> = ({ displayName, avatarUrl, isMuted, isLocal }) => {
  return (
    <div className="bg-zinc-900/90 border border-zinc-800 rounded-2xl p-6 flex flex-col items-center justify-center space-y-4 shadow-xl relative min-w-[180px]">
      <div className="relative">
        {!isMuted && (
          <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
        )}
        <UserAvatar
          displayName={displayName}
          avatarUrl={avatarUrl}
          size="2xl"
          className="relative shadow-lg"
        />
        {isMuted && (
          <div className="absolute -bottom-1 -right-1 bg-red-500 text-white p-1.5 rounded-full shadow-md z-10">
            <MicOff className="h-3.5 w-3.5" />
          </div>
        )}
      </div>

      <div className="text-center">
        <h4 className="font-bold text-sm text-white">{displayName} {isLocal && '(You)'}</h4>
        <p className="text-[11px] text-zinc-400 font-medium">
          {isMuted ? 'Muted' : 'Speaking...'}
        </p>
      </div>
    </div>
  );
};

export const GroupCallGrid: React.FC = () => {
  const { user } = useAuth();
  const {
    activeGroupRoom,
    participants,
    localStream,
    remoteStreams,
    screenStream,
    isJoined,
    isMuted,
    isCameraOff,
    isScreenSharing,
    callState,
    roomFullError,
    leaveGroupCall,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    clearRoomFullError,
  } = useGroupCall();

  if (roomFullError) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
        <div className="bg-card border border-border p-6 rounded-2xl max-w-sm w-full text-center shadow-2xl">
          <div className="w-12 h-12 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h3 className="font-bold text-lg text-foreground mb-2">Call Error</h3>
          <p className="text-xs text-muted-foreground mb-6">{roomFullError}</p>
          <button
            onClick={clearRoomFullError}
            className="w-full py-2 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // Render starting overlay while room initialization is in progress
  if (callState === 'STARTING' && !activeGroupRoom) {
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950 flex items-center justify-center p-4 select-none">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <div className="w-12 h-12 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
              <Phone className="h-6 w-6 animate-pulse" />
            </div>
          </div>
          <div>
            <h3 className="font-bold text-lg text-white">Starting Channel Call...</h3>
            <p className="text-xs text-zinc-400">Connecting to server and ringing team members</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isJoined || !activeGroupRoom || !activeGroupRoom.roomId) return null;

  const isVideoCall = activeGroupRoom.callType === 'VIDEO';
  const remoteParticipants = participants.filter((p) => p.userId !== user?.id);
  const totalCount = remoteParticipants.length + 1;

  let gridColsClass = 'grid-cols-1';
  if (totalCount === 2) gridColsClass = 'grid-cols-1 md:grid-cols-2';
  else if (totalCount >= 3) gridColsClass = 'grid-cols-1 md:grid-cols-2';

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col p-4 select-none overflow-hidden">
      {/* Hidden audio elements for all remote participants to guarantee audible audio playback */}
      {remoteParticipants.map((p) => (
        <RemoteAudioPlayer
          key={p.userId}
          userId={p.userId}
          stream={remoteStreams.get(p.userId) || null}
        />
      ))}

      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 bg-zinc-900/60 backdrop-blur-md rounded-xl border border-zinc-800 mb-2">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-bold text-sm text-white">
            Channel {isVideoCall ? 'Video' : 'Audio'} Call ({totalCount}/4 Participants)
          </span>
          {totalCount === 1 && (
            <span className="text-xs bg-primary/20 text-primary px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1.5 animate-pulse">
              <span>Calling team members...</span>
            </span>
          )}
        </div>
        <span className="text-xs text-zinc-400 font-mono">
          Room #{activeGroupRoom.roomId.substring(0, 8)}
        </span>
      </div>

      {/* Main Viewport */}
      <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center py-2">
        {isVideoCall || isScreenSharing ? (
          /* Video Grid Mode */
          <div className={`grid ${gridColsClass} gap-4 max-w-6xl w-full max-h-full overflow-y-auto px-2`}>
            {/* Local Participant Tile */}
            <VideoTile
              stream={isScreenSharing && screenStream ? screenStream : localStream}
              displayName={user?.displayName || 'You'}
              avatarUrl={user?.avatarUrl || undefined}
              isMuted={isMuted}
              isVideoOff={isCameraOff && !isScreenSharing}
              isScreenSharing={isScreenSharing}
              isLocal
            />

            {/* Remote Participant Tiles */}
            {remoteParticipants.map((p) => {
              const rStream = remoteStreams.get(p.userId) || null;
              return (
                <VideoTile
                  key={p.userId}
                  stream={rStream}
                  displayName={p.displayName || 'Participant'}
                  avatarUrl={p.avatarUrl || undefined}
                  isMuted={p.audioMuted}
                  isVideoOff={p.videoOff && !p.screenSharing}
                  isScreenSharing={p.screenSharing}
                />
              );
            })}
          </div>
        ) : (
          /* Audio Huddle Mode: Clean Participant Cards with Dedicated Remote Audio Players */
          <div className="flex flex-wrap items-center justify-center gap-6 max-w-4xl w-full p-4">
            <AudioTile
              displayName={user?.displayName || 'You'}
              avatarUrl={user?.avatarUrl}
              isMuted={isMuted}
              isLocal
            />
            {remoteParticipants.map((p) => (
              <AudioTile
                key={p.userId}
                displayName={p.displayName || 'Participant'}
                avatarUrl={p.avatarUrl}
                isMuted={p.audioMuted}
              />
            ))}
          </div>
        )}
      </div>

      {/* Floating Control Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-center gap-4 pt-2 pb-1 z-20">
        <div className="flex items-center gap-3 px-6 py-2.5 bg-zinc-900/90 border border-zinc-800 backdrop-blur-md rounded-2xl shadow-2xl">
          {/* Mute/Unmute */}
          <button
            onClick={toggleMute}
            title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
            className={`p-3 rounded-xl transition ${
              isMuted ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-zinc-800 text-white hover:bg-zinc-700'
            }`}
          >
            {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </button>

          {/* Camera On/Off — Visible ONLY in Video Calls */}
          {isVideoCall && (
            <button
              onClick={toggleCamera}
              title={isCameraOff ? 'Turn Camera On' : 'Turn Camera Off'}
              className={`p-3 rounded-xl transition ${
                isCameraOff ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              {isCameraOff ? <VideoOff className="h-5 w-5" /> : <VideoIcon className="h-5 w-5" />}
            </button>
          )}

          {/* Screen Share — Visible ONLY in Video Calls (or active sharing) */}
          {(isVideoCall || isScreenSharing) && (
            <button
              onClick={toggleScreenShare}
              title={isScreenSharing ? 'Stop Screen Share' : 'Share Screen'}
              className={`p-3 rounded-xl transition ${
                isScreenSharing ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              <Monitor className="h-5 w-5" />
            </button>
          )}

          {/* Leave Call */}
          <button
            onClick={leaveGroupCall}
            title="Leave Call"
            className="p-3 rounded-xl bg-red-600 text-white hover:bg-red-700 transition font-bold"
          >
            <PhoneOff className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
